import { useCallback, useState } from "react";
import { Button, DatePicker, Flex, InfoBox, ModalV2, Text } from "@jsq/ds/core";
import { useExportManuallyEditedEntityTransactionsMutation } from "~/graphql/supergraphHooks";
import styles from "../styles_vanilla.css";

const DATE_RANGE_LIMIT = 100;
const DATE_RANGE_LIMIT_MS = DATE_RANGE_LIMIT * 24 * 60 * 60 * 1000;

export type TransactionExportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  openSuccessModal: () => void;
};

export const TransactionExportModal = ({
  isOpen,
  onClose,
  openSuccessModal,
}: TransactionExportModalProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [exportTransactionReport] = useExportManuallyEditedEntityTransactionsMutation();

  const handleOnClose = useCallback(() => {
    onClose();
    setIsLoading(false);
    setStartDate(null);
    setEndDate(null);
    setStartDateError("");
    setEndDateError("");
    setErrorMessage("");
  }, []);

  const handleStartDateSelect = useCallback((_startDate: Date | null) => {
    setStartDateError("");
    setStartDate(_startDate);
  }, []);
  const handleEndDateSelect = useCallback((_endDate: Date | null) => {
    setEndDateError("");
    setEndDate(_endDate);
  }, []);

  const handleExportClick = useCallback(() => {
    setErrorMessage("");

    if (!startDate || !endDate) {
      if (!startDate) {
        setStartDateError("Please select a start date");
      }
      if (!endDate) {
        setEndDateError("Please select an end date");
      }
      return;
    }
    if (endDate < startDate) {
      setErrorMessage("The start date cannot be later than the end date");
      return;
    } else if (endDate > new Date() || startDate > new Date()) {
      setErrorMessage("The date cannot be later than today");
      return;
    } else if (endDate.getTime() - startDate.getTime() > DATE_RANGE_LIMIT_MS) {
      setErrorMessage(`Date range must not exceed ${DATE_RANGE_LIMIT} days`);
      return;
    }

    setIsLoading(true);
    void exportTransactionReport({
      variables: {
        startDate: startDate.toISOString().split("T")[0],
        endDate: endDate.toISOString().split("T")[0],
      },
    })
      .then((_) => {
        handleOnClose();
        openSuccessModal();
      })
      .finally(() => setIsLoading(false));
  }, [exportTransactionReport, startDate, endDate, handleOnClose, openSuccessModal]);

  return (
    <ModalV2
      show={isOpen}
      onHide={handleOnClose}
      size="small"
      title="Report date range"
      description="Date range limited to 100 days. Please allow up to 30 minutes for the report to
          generate. You will receive an email notification once complete."
    >
      <ModalV2.Body>
        <div className={styles.modalDatePicker}>
          <Text>Start date</Text>
          <DatePicker
            selected={startDate}
            onChange={handleStartDateSelect}
            hasIcon
            isFull
            errorMessage={startDateError}
          />
        </div>
        <div className={styles.modalDatePicker}>
          <Text>End date</Text>
          <DatePicker
            selected={endDate}
            onChange={handleEndDateSelect}
            hasIcon
            isFull
            errorMessage={endDateError}
          />
        </div>
        {errorMessage && (
          <InfoBox className={styles.modalError} variant="error" title={errorMessage} />
        )}
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex flexDirection="row" flexWrap="nowrap" justifyContent="flex-end">
          <Button onClick={handleOnClose} variant="neutral">
            Cancel
          </Button>
          <Button onClick={handleExportClick} variant="primary" isLoading={isLoading}>
            Export report (.csv)
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
